import { useCallback } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { markWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { goInNewTab } from '~/utils/urlHelper';

interface WebsiteVisitActionInput {
    /** The item's website link, or null/undefined when it has none. */
    website?: string | null;
    /** The library item the referral is attributed to. */
    libraryItemID: number;
    professionalID: number;
    professionalTitle: string;
    /** CTA surface, landing as `Source` on the `Website` event. */
    source: string;
}

/** Params the tracker strips before reporting the destination URL. */
const TRACKING_PARAMS = [
    '_ap_pageid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    '__apid',
];

/** The destination as reported to analytics: the link without our own params. */
const toExternalURL = (trackedURL: string): string => {
    try {
        const url = new URL(trackedURL);
        TRACKING_PARAMS.forEach((param) => url.searchParams.delete(param));
        return url.toString();
    } catch {
        return trackedURL;
    }
};

/**
 * A "Visit site" action for surfaces that cannot use an anchor — chiefly the
 * directory tiles, whose overlay renders inside the tile's own link (nested
 * anchors are invalid HTML and get split by the parser).
 *
 * `websiteURL` is the tracked link (UTM + `__apid`); `visitWebsite` logs the
 * same `Website` event the anchor-based CTAs produce through the tracker's
 * global link listener, then opens the site in a new tab. Returns a null
 * `websiteURL` when there is no usable website, which is the caller's cue to
 * render nothing.
 */
export const useWebsiteVisitAction = ({
    website,
    libraryItemID,
    professionalID,
    professionalTitle,
    source,
}: WebsiteVisitActionInput) => {
    const tracker = useTracker();
    const { referralTrackingURL } = useWebsiteReferralTracking(website, {
        libraryItemID,
        professionalID,
        professionalTitle,
        source,
    });

    const visitWebsite = useCallback(() => {
        if (!referralTrackingURL) return;

        void tracker.log('Website', {
            url: new URL(window.location.href),
            label: professionalTitle,
            data: {
                LibraryItemID: libraryItemID,
                ProfessionalID: professionalID,
                ExternalURL: toExternalURL(referralTrackingURL),
                Source: source,
                // Only left clicks reach a button handler, so the button never
                // reports the middle / right clicks an anchor can.
                ExtraData: { MouseButton: 0 },
            },
        });

        markWebsiteVisited(libraryItemID);
        goInNewTab(referralTrackingURL);
    }, [
        referralTrackingURL,
        tracker,
        libraryItemID,
        professionalID,
        professionalTitle,
        source,
    ]);

    return {
        websiteURL: referralTrackingURL ?? null,
        visitWebsite,
    } as const;
};
